import React, { useMemo } from 'react';
import clsx from 'clsx';
import { PaintAreaMarkerProps } from './VehiclePaintMeasurement.types';
import { paintStatusConfig, formatMeasurementRange } from './paintStatusConfig';

/**
 * Komponent renderujący pojedynczy obszar lakieru
 *
 * Wyświetla:
 * - Kolorowany obszar SVG z odpowiednim wypełnieniem i obramowaniem
 * - Etykietę z literą obszaru i opcjonalnie wartością pomiaru
 *
 * Kolor i obramowanie są dynamicznie przypisywane na podstawie statusu pomiaru.
 * Gdy `isHighlighted` jest true, marker zmienia tylko KOLORY (fill/stroke obszaru SVG
 * oraz obramowanie etykiety na niebieski) i pokazuje subtelny radialny gradient. Rozmiary
 * obrysów i etykiety pozostają niezmienione, żeby uniknąć przesuwania się treści.
 */
export const PaintAreaMarker: React.FC<PaintAreaMarkerProps> = ({
  position,
  status,
  areaDefinition,
  minMeasurement,
  maxMeasurement,
  isMobile = false,
  autoScale = 1,
  isHighlighted = false,
  onHover,
  onClick,
}) => {
  const baseConfig = paintStatusConfig[status];
  // Przy aktywacji nadpisujemy kolor wypełnienia + obramowania na niebieski
  const HIGHLIGHT_COLOR = '#0064FF';
  const config = isHighlighted
    ? { ...baseConfig, fillColor: HIGHLIGHT_COLOR, strokeColor: HIGHLIGHT_COLOR }
    : baseConfig;
  const { SVGComponent, positionX, positionY, labelX, labelY, scale } = areaDefinition;
  const manualScale = scale?.[isMobile ? 'mobile' : 'desktop'] ?? 1;
  const effectiveScale = autoScale * manualScale;

  // Gdy kontener jest węższy niż designWidth, car SVG shrinkuje uniformowo (preserveAspectRatio meet),
  // ale fills container width — letterbox jest tylko w osi Y. Korygujemy więc tylko Y, X bez zmian.
  const topPct = 50 + autoScale * (positionY - 50);

  const gradientStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, ${config.strokeColor} 0%, transparent 70%)`,
    }),
    [config.strokeColor],
  );

  const isInteractive = Boolean(onHover || onClick);

  return (
    <div
      className={clsx(
        'absolute origin-center z-10 transition-transform',
        isInteractive && 'pointer-events-auto cursor-pointer',
      )}
      style={{
        left: `${positionX}%`,
        top: `${topPct}%`,
        transform: `translate(-50%, -50%) scale(${effectiveScale})`,
      }}
      onMouseEnter={onHover ? () => onHover(position) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      onClick={onClick ? () => onClick(position) : undefined}
    >
      {/* Subtelny gradient glow widoczny tylko przy highlightcie */}
      <div
        className="absolute inset-0 rounded-full transition-opacity duration-200 pointer-events-none"
        style={{
          ...gradientStyle,
          opacity: isHighlighted ? 0.45 : 0,
          transform: 'scale(1.05)',
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* Komponent SVG obszaru z dynamicznymi kolorami */}
      <SVGComponent
        fill={config.fillColor}
        stroke={config.strokeColor}
        strokeWidth={2}
        fillOpacity={isHighlighted ? 0.4 : 0.5}
        className="transition-all duration-200"
      />

      {/* Etykieta z pozycją i wartością */}
      <div
        className="flex absolute flex-col justify-center items-center pointer-events-none"
        style={{
          left: `${labelX}%`,
          top: `${labelY}%`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div
          className="flex absolute justify-center items-center font-bold text-white rounded-full shadow-lg"
          style={{
            width: '27px',
            height: '27px',
            fontSize: '12px',
            backgroundColor: baseConfig.strokeColor,
            zIndex: 2,
            top: '-16px',
            right: '-14px',
          }}
        >
          {position}
        </div>
        {/* Tło dla etykiety */}
        <div
          className="flex flex-col justify-center items-center px-2 py-1 rounded border-2 transition-[border-color,box-shadow] duration-200"
          style={{
            backgroundColor: '#111438',
            borderColor: config.strokeColor,
            opacity: 0.95,
            boxShadow: isHighlighted ? '0 0 20px 0 #0064FF' : undefined,
          }}
        >
          {/* Tekst - zakres wartości z API lub "brak" dla MISSING/NOT_APPLICABLE */}
          {status === 'MISSING' || status === 'NOT_APPLICABLE' ? (
            <span
              className="text-[14px] font-bold select-none mt-0.5 whitespace-nowrap"
              style={{ color: config.textColor }}
            >
              BRAK
            </span>
          ) : minMeasurement != null && maxMeasurement != null ? (
            <span
              className="text-[14px] font-bold select-none mt-0.5 whitespace-nowrap"
              style={{ color: config.textColor }}
            >
              {formatMeasurementRange(minMeasurement, maxMeasurement)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
